import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listPrintRuns, listPrinterProfiles, type Batch, type PrinterProfile, type PrintRun, type PrintSpool } from "@/api/client";
import { useSession } from "@/auth/session";
import { formatDate } from "@/components/format";
import { EmptyState, ErrorBanner } from "@/components/ui";
import { exportBaseName } from "@/features/batches/BatchExport";
import { SpoolRow } from "@/features/batches/SpoolRow";
import { ScrollArea } from "@/components/ScrollArea";

/** How often the runs are refetched while a spool is being built or sent. */
export const PRINT_RUNS_POLL_MS = 3_000;

const ACTIVE_SPOOL_STATUSES: readonly PrintSpool["status"][] = ["queued", "sending"];

export const printRunsKey = (batchId: string) => ["print-runs", batchId] as const;

export function hasActiveSpools(runs: PrintRun[] | undefined) {
  return (runs ?? []).some((run) => run.spools.some((spool) => ACTIVE_SPOOL_STATUSES.includes(spool.status)));
}

export function usePrintRuns(batchId: string) {
  return useQuery({
    queryKey: printRunsKey(batchId),
    queryFn: () => listPrintRuns(batchId),
    refetchInterval: (query) => (hasActiveSpools(query.state.data) ? PRINT_RUNS_POLL_MS : false),
  });
}

export function optionsLabel(options: PrintRun["options"]) {
  const m = options.margins_mm;
  return [
    `${options.paper_size} ${options.orientation === "L" ? "landscape" : "portrait"}`,
    `offset X ${options.offset_x_mm} mm, Y ${options.offset_y_mm} mm`,
    `margins ${m.top}/${m.right}/${m.bottom}/${m.left} mm (top/right/bottom/left)`,
    options.preprinted_stock ? "pre-printed stock" : "plain stock",
  ].join(", ");
}

export function PrintRunsPanel({ batch }: { batch: Batch }) {
  const { can } = useSession();
  const canPrint = can("certificate.print");
  const queryClient = useQueryClient();
  const runs = usePrintRuns(batch.id);
  const items = runs.data ?? [];
  // Runs name their profile but not its IPP state; archived profiles still own runs.
  const profiles = useQuery({
    queryKey: ["printer-profiles", { archived: true }],
    queryFn: () => listPrinterProfiles({ includeArchived: true }),
    enabled: canPrint,
  });
  // The API doesn't say a spool was rebuilt, so this only covers retries made on this page.
  const [rebuilt, setRebuilt] = useState<ReadonlySet<string>>(new Set());
  const mark = (id: string, on: boolean) =>
    setRebuilt((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  const refetch = () => queryClient.invalidateQueries({ queryKey: printRunsKey(batch.id) });

  return (
    <section className="space-y-3 rounded-md border border-zinc-200 p-3">
      <div>
        <h3 className="text-sm font-semibold">Print runs</h3>
        <p className="mt-1 text-xs text-zinc-500">Newest first. A spool is one sub-batch's print-ready PDF.</p>
      </div>
      <ErrorBanner error={runs.error ?? profiles.error} />
      {hasActiveSpools(runs.data) ? (
        <p role="status" className="text-xs text-sky-700">
          Spools are being built or sent. Checking for updates every few seconds.
        </p>
      ) : null}
      {runs.isLoading ? <EmptyState title="Loading print runs" detail="Fetching the batch's print runs." compact /> : null}
      {runs.isSuccess && items.length === 0 ? (
        <EmptyState title="No print runs yet" detail="A print run splits the batch into spools to download or send." compact />
      ) : null}
      {items.map((run, index) => (
        <PrintRunItem
          key={run.id}
          run={run}
          number={items.length - index}
          baseName={exportBaseName(batch)}
          canPrint={canPrint}
          profile={profiles.data?.find((p) => p.id === run.printer_profile.id)}
          rebuilt={rebuilt}
          onRebuilt={mark}
          onChanged={refetch}
        />
      ))}
    </section>
  );
}

function PrintRunItem({
  run,
  number,
  baseName,
  canPrint,
  profile,
  rebuilt,
  onRebuilt,
  onChanged,
}: {
  run: PrintRun;
  number: number;
  baseName: string;
  canPrint: boolean;
  profile?: PrinterProfile;
  rebuilt: ReadonlySet<string>;
  onRebuilt: (spoolId: string, on: boolean) => void;
  onChanged: () => Promise<void>;
}) {
  const label = `Print run ${number}`;
  return (
    <article aria-label={label} className="space-y-2 rounded-md bg-zinc-50 p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h4 className="text-sm font-medium">
          {label}: <span>{run.printer_profile.name}</span>
        </h4>
        <span className="text-xs text-zinc-500">{`By ${run.created_by_name ?? "unknown"}, ${formatDate(run.created_at)}`}</span>
      </div>
      <p className="flex flex-wrap gap-x-3 text-xs text-zinc-600">
        <span>{`${run.sub_batch_size} per spool`}</span>
        <span>{run.send_to_printer ? "Sends to the printer" : "Download only"}</span>
      </p>
      <p className="text-xs text-zinc-500">{optionsLabel(run.options)}</p>
      {canPrint && profile && !profile.ipp_allowed ? (
        <p className="text-xs text-zinc-500">
          {run.printer_profile.name} has no IPP address the server may contact, so its spools are downloaded and printed by hand.
        </p>
      ) : null}
      <ScrollArea label={`Spools of ${label.toLowerCase()}`}>
        <table aria-label={`Spools of ${label.toLowerCase()}`} className="w-full min-w-[760px] text-left text-xs">
          <thead className="border-b border-zinc-200 text-zinc-500">
            <tr>
              <th className="px-2 py-2 font-medium">Spool</th>
              <th className="px-2 py-2 font-medium">Certificates</th>
              <th className="px-2 py-2 font-medium">Count</th>
              <th className="px-2 py-2 font-medium">Status</th>
              <th className="px-2 py-2 font-medium">Attempts</th>
              <th className="px-2 py-2 font-medium">IPP job</th>
              <th className="px-2 py-2 font-medium">Last error</th>
              <th className="px-2 py-2 font-medium">Sent</th>
              <th className="px-2 py-2 font-medium">Printed</th>
              <th className="px-2 py-2 font-medium">{canPrint ? "Actions" : ""}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-100">
            {run.spools.map((spool) => (
              <SpoolRow
                key={spool.id}
                spool={spool}
                filename={`${baseName}-run-${number}-spool-${spool.seq}.pdf`}
                canPrint={canPrint}
                canSend={profile?.ipp_allowed ?? false}
                sendRun={run.send_to_printer}
                rebuilt={rebuilt.has(spool.id)}
                onRetried={() => onRebuilt(spool.id, true)}
                onSent={() => onRebuilt(spool.id, false)}
                onChanged={onChanged}
              />
            ))}
          </tbody>
        </table>
      </ScrollArea>
    </article>
  );
}
